import { AlertCircle, CheckCircle2, ClipboardList, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { useAuth } from "@/app/context/Auth/AuthContext";
import { cn } from "@/app/handlers/cn.utils";
import {
	getEndOfLastMonth,
	getStartOfLastMonth,
	getStartOfMonth,
	getStartOfWeek,
} from "@/app/handlers/utilityFunctions";

import Loader from "../../components/Loader";
import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import Header from "../Profile/components/Header";
import FitCheckCard from "./components/FitCheckCard";
import FitCheckDetailDrawer from "./components/FitCheckDetailDrawer";
import StatsCard from "./components/StatsCard";
import { AssessmentRecord, useAssessments } from "./config/api";
import { isReadyToApply, requiredStats } from "./config/stats";

type FilterTab = "all" | "ready" | "prepare";

function FitCheckDashboard() {
	const navigate = useNavigate();
	const isLoggedIn = useIsLoggedIn();
	const { userData } = useAuth();
	const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
	const [selectedRecord, setSelectedRecord] = useState<AssessmentRecord | null>(null);
	const [isScrolled, setIsScrolled] = useState(false);

	const { data: records, isLoading, isError } = useAssessments(userData?.userID || "");

	useEffect(() => {
		const handleScroll = () => setIsScrolled(window.scrollY > 0);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const stats = useMemo(() => {
		if (!records) return { total: 0, mustHavesMet: 0, credits: 5, usedThisMonth: 0, percentChange: 0 };

		const now = new Date();
		const startOfWeek = getStartOfWeek(now);
		const startOfMonth = getStartOfMonth(now);
		const startOfLastMonth = getStartOfLastMonth(now);
		const endOfLastMonth = getEndOfLastMonth(now);

		let mustHavesMet = 0;
		let usedThisWeek = 0;
		let usedThisMonth = 0;
		let usedLastMonth = 0;

		records.forEach((record) => {
			mustHavesMet += requiredStats(record.assessment).met;

			const createdAt = new Date(record.createdAt);
			if (createdAt >= startOfWeek) usedThisWeek++;
			if (createdAt >= startOfMonth) usedThisMonth++;
			if (createdAt >= startOfLastMonth && createdAt <= endOfLastMonth) usedLastMonth++;
		});

		let percentChange = 0;
		if (usedLastMonth > 0) {
			percentChange = Math.round(((usedThisMonth - usedLastMonth) / usedLastMonth) * 100);
		} else if (usedThisMonth > 0) {
			percentChange = 100;
		}

		const credits = Math.max(0, 5 - usedThisWeek);

		return { total: records.length, mustHavesMet, credits, usedThisMonth, percentChange };
	}, [records]);

	const filterCounts = useMemo(() => {
		if (!records) return { all: 0, ready: 0, prepare: 0 };

		let readyCount = 0;
		records.forEach((record) => {
			if (isReadyToApply(record.assessment)) readyCount++;
		});

		return { all: records.length, ready: readyCount, prepare: records.length - readyCount };
	}, [records]);

	const filteredRecords = useMemo(() => {
		if (!records) return [];
		return records.filter((record) => {
			if (activeFilter === "all") return true;
			if (activeFilter === "ready") return isReadyToApply(record.assessment);
			return !isReadyToApply(record.assessment);
		});
	}, [records, activeFilter]);

	const filterTabs: { key: FilterTab; label: string; count: number; variant: "default" | "ready" | "prepare" }[] = [
		{ key: "all", label: "All FitChecks", count: filterCounts.all, variant: "default" },
		{ key: "ready", label: "Ready to Apply", count: filterCounts.ready, variant: "ready" },
		{ key: "prepare", label: "Prepare First", count: filterCounts.prepare, variant: "prepare" },
	];

	return (
		<>
			<FitCheckDetailDrawer record={selectedRecord} onClose={() => setSelectedRecord(null)} />

			<div className={`sticky top-0 z-50 bg-white transition-all duration-300 ${isScrolled ? "shadow-xs" : ""}`}>
				<Header isLoggedIn={isLoggedIn} variant="applicant" />
			</div>

			<div className="min-h-screen bg-gray-50">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
						<StatsCard
							title="Total FitChecks"
							value={stats.total}
							subtitle={
								stats.percentChange >= 0
									? `+${stats.percentChange}% from last month`
									: `${stats.percentChange}% from last month`
							}
							subtitleColor={stats.percentChange >= 0 ? "text-green-600" : "text-red-500"}
							icon={<ClipboardList className="w-6 h-6" />}
						/>
						<StatsCard
							title="Must-haves met"
							value={stats.mustHavesMet}
							subtitle={`+${stats.usedThisMonth} checks this month`}
							subtitleColor="text-green-600"
							icon={<CheckCircle2 className="w-6 h-6" />}
						/>
						<StatsCard
							title="Credits Available"
							value={stats.credits}
							subtitle={`${stats.usedThisMonth} used this month`}
							subtitleColor="text-red-500"
							icon={
								<div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center">
									<span className="text-yellow-600 text-xs font-bold">$</span>
								</div>
							}
						/>
					</div>

					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
						<div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
							{filterTabs.map((tab) => {
								const isActive = activeFilter === tab.key;
								let bgClass = "bg-white border border-gray-200";
								let textClass = "text-gray-600";
								let countBgClass = "bg-gray-100";

								if (isActive) {
									textClass = "text-white";
									countBgClass = "bg-white/20";
									if (tab.variant === "ready") bgClass = "bg-green-600";
									else if (tab.variant === "prepare") bgClass = "bg-blue-600";
									else bgClass = "bg-primary-600";
								}

								return (
									<button
										key={tab.key}
										onClick={() => setActiveFilter(tab.key)}
										className={cn(
											"px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2",
											bgClass,
											textClass,
										)}
									>
										{tab.label}
										<span
											className={cn(
												"px-2 py-0.5 rounded-full text-xs",
												countBgClass,
												isActive ? "text-white" : "text-gray-500",
											)}
										>
											{tab.count}
										</span>
									</button>
								);
							})}
						</div>

						<Button
							onClick={() => navigate("/jobs")}
							className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2"
						>
							<Plus className="w-4 h-4" />
							New FitCheck
						</Button>
					</div>

					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<Loader />
						</div>
					) : isError ? (
						<div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
							<AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
							<p className="text-red-700">Failed to load FitCheck history. Please try again.</p>
							<Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
								Retry
							</Button>
						</div>
					) : filteredRecords.length === 0 ? (
						<div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
							<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<CheckCircle2 className="w-8 h-8 text-gray-400" />
							</div>
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								{activeFilter === "all"
									? "No FitChecks yet"
									: `No ${filterTabs.find((t) => t.key === activeFilter)?.label} FitChecks`}
							</h3>
							<p className="text-gray-500 mb-6 max-w-sm mx-auto">
								{activeFilter === "all"
									? "Run your first FitCheck from a job listing to see how well you match."
									: "Try changing the filter to see other FitChecks."}
							</p>
							{activeFilter === "all" && (
								<Button onClick={() => navigate("/jobs")} className="bg-primary-600 hover:bg-primary-700">
									Browse Jobs
								</Button>
							)}
						</div>
					) : (
						<div className="space-y-4">
							{filteredRecords.map((record) => (
								<FitCheckCard
									key={`${record.jobId}-${record.createdAt}`}
									record={record}
									onViewDetails={() => setSelectedRecord(record)}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export default FitCheckDashboard;
